"use client";

import * as React from "react";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import {
  KEY_ARROW_DOWN_COMMAND,
  KEY_ARROW_UP_COMMAND,
  COMMAND_PRIORITY_LOW,
  $getNodeByKey,
  $getRoot,
  $getSelection,
  $isRangeSelection,
  $isNodeSelection,
  $isTextNode,
  $isParagraphNode,
  type LexicalNode,
} from "lexical";
import { $isHeadingNode } from "@lexical/rich-text";
import { $isListItemNode } from "@lexical/list";
import { $isTableNode } from "@lexical/table";
import { findClosestBlockNodeKey } from "../../kernel/helpers/findClosestBlockNodeKey";
import { $ensureTailEmptyParagraphAndSelect } from "../../kernel/helpers/ensureTailEmptyParagraph";
import { $focusImageBlockNode, $isImageBlockNode } from "../../kernel/nodes/ImageBlockNode";

function $rootTopBlocksInOrder(root: ReturnType<typeof $getRoot>): LexicalNode[] {
  return root.getChildren().filter((n) => $isParagraphNode(n) || $isHeadingNode(n) || $isTableNode(n) || $isImageBlockNode(n));
}

function $focusTopBlock(node: LexicalNode): void {
  if ($isImageBlockNode(node)) $focusImageBlockNode(node);
  else (node as { selectStart?: () => void }).selectStart?.();
}

export function KeyboardBlockNavigationPlugin({ isEditable }: { isEditable: boolean }) {
  const [editor] = useLexicalComposerContext();

  React.useEffect(() => {
    if (!isEditable) return;

    return editor.registerCommand(
      KEY_ARROW_UP_COMMAND,
      (payload: KeyboardEvent) => {
        if (payload.shiftKey) return false;

        const selection = $getSelection();
        if (!selection) return false;

        if ($isNodeSelection(selection)) {
          const nodes = selection.getNodes();
          if (nodes.length !== 1 || !$isImageBlockNode(nodes[0])) return false;
          const key = nodes[0].getKey();
          const root = $getRoot();
          const topBlocks = $rootTopBlocksInOrder(root);
          const idx = topBlocks.findIndex((n) => n.getKey() === key);
          if (idx <= 0) return false;
          payload.preventDefault();
          editor.update(() => {
            $focusTopBlock(topBlocks[idx - 1]);
          });
          return true;
        }

        if (!$isRangeSelection(selection)) return false;
        if (!selection.isCollapsed) return false;

        const anchorNode = selection.anchor.getNode();
        if (!$isTextNode(anchorNode)) return false;

        const anchorOffset = selection.anchor.offset;
        if (anchorOffset !== 0) return false;

        const key = findClosestBlockNodeKey(anchorNode);
        if (!key) return false;
        const node = $getNodeByKey(key);
        if ($isListItemNode(node)) return false;

        const root = $getRoot();
        const topBlocks = $rootTopBlocksInOrder(root);
        const idx = topBlocks.findIndex((n) => n.getKey() === key);
        if (idx <= 0) return false;

        payload.preventDefault();
        editor.update(() => {
          $focusTopBlock(topBlocks[idx - 1]);
        });
        return true;
      },
      COMMAND_PRIORITY_LOW,
    );
  }, [editor, isEditable]);

  React.useEffect(() => {
    if (!isEditable) return;

    return editor.registerCommand(
      KEY_ARROW_DOWN_COMMAND,
      (payload: KeyboardEvent) => {
        if (payload.shiftKey) return false;

        const selection = $getSelection();
        if (!selection) return false;

        if ($isNodeSelection(selection)) {
          const nodes = selection.getNodes();
          if (nodes.length !== 1 || !$isImageBlockNode(nodes[0])) return false;
          const key = nodes[0].getKey();
          const root = $getRoot();
          const topBlocks = $rootTopBlocksInOrder(root);
          const idx = topBlocks.findIndex((n) => n.getKey() === key);
          if (idx < 0) return false;
          payload.preventDefault();
          if (idx < topBlocks.length - 1) {
            editor.update(() => {
              $focusTopBlock(topBlocks[idx + 1]);
            });
          } else {
            editor.update(() => {
              $ensureTailEmptyParagraphAndSelect();
            });
          }
          return true;
        }

        if (!$isRangeSelection(selection)) return false;
        if (!selection.isCollapsed) return false;

        const anchorNode = selection.anchor.getNode();
        if (!$isTextNode(anchorNode)) return false;

        const key = findClosestBlockNodeKey(anchorNode);
        if (!key) return false;
        const node = $getNodeByKey(key);
        if ($isListItemNode(node)) return false;

        const anchorOffset = selection.anchor.offset;
        const textLen = anchorNode.getTextContentSize?.() ?? anchorNode.getTextContent?.().length ?? 0;
        if (anchorOffset !== textLen) return false;

        const root = $getRoot();
        const topBlocks = $rootTopBlocksInOrder(root);
        const idx = topBlocks.findIndex((n) => n.getKey() === key);
        if (idx < 0) return false;

        payload.preventDefault();
        if (idx < topBlocks.length - 1) {
          editor.update(() => {
            $focusTopBlock(topBlocks[idx + 1]);
          });
          return true;
        }

        editor.update(() => {
          $ensureTailEmptyParagraphAndSelect();
        });
        return true;
      },
      COMMAND_PRIORITY_LOW,
    );
  }, [editor, isEditable]);

  return null;
}
